<template>
  <component
    :is="product.Slug ? 'a' : 'div'"
    class="static-product-tile-fi"
    :href
    @click.stop.prevent="$emit('go-to-product', getItemQuantity())"
  >
    <div class="static-product-tile-fi__image">
      <cloud-image-fi
        v-if="product.image"
        :src="product.image"
        :alt="product.name"
        :aspect-ratio="1"
        trim
        params="func=fit"
        class="static-product-tile-fi__img"
        do-not-replace-url
      />
    </div>

    <div class="static-product-tile-fi__artnr">
      {{ $dict.get('construction-Projects.Global-Labels.Article-Number', [product.id]) }}
    </div>
    <div class="static-product-tile-fi__name">
      {{ product.name }}
    </div>

    <div class="static-product-tile-fi__action">
      <template v-if="!isFite">
        <template v-if="isShop">
          <div class="static-product-tile-fi__product-facts">
            <loading-indicator-wrapper
              class="static-product-tile-fi__loading-indicator"
              :loading="bestPriceLoading || loading"
              @click.stop.prevent
            >
              <div
                :class="{
                  'static-product-tile-fi__details': true,
                  'static-product-tile-fi__details--without-price': showWithoutPrice,
                }"
              >
                <div class="static-product-tile-fi__details__top">
                  <div class="static-product-tile-fi__price-and-delivery-details">
                    <price-savings-fi
                      class="static-product-tile-fi__price"
                      :best-price="currentBestPrice"
                      theme="product-list-view"
                      hide-savings-in-percent
                      stroke-price-absolute
                    />

                    <div
                      v-if="!showWithoutPrice"
                      class="static-product-tile-fi__vac"
                    >
                      {{ $dict.get('online-Shop.Product.Vat-excluded') }}
                    </div>

                    <div class="static-product-tile-fi__availability-status-container">
                      <availability-status
                        v-if="!bestPriceLoading"
                        :class="{
                          'static-product-tile-fi__availability-status': true,
                          'fi-mt-9': !currentBestPrice,
                        }"
                        :delivery-speed="
                          currentBestPrice ? currentBestPrice.deliverySpeed : 'NODELIVERY'
                        "
                        :stock-levels="currentBestPrice?.stockLevels || null"
                        theme="product-list-view"
                        show-status-text
                        disable-tooltip
                      />
                    </div>
                  </div>

                  <div
                    v-if="!bestPriceLoading && isSM"
                    class="static-product-tile-fi__add-to-cart"
                  >
                    <button-fi
                      :data-productid="product ? product.id : null"
                      :data-productname="product ? `${product.id} - ${product.name}` : null"
                      :disabled="!bestPrice || isAddingItemToCart[product.id] || outOfStock"
                      type="button"
                      class="button-fi__primary static-product-tile-fi__action-button static-product-tile-fi__action-button--onlineShop"
                      :class="!showWithoutPrice ? 'button-fi__icon' : 'button-fi--stretched'"
                      data-test="product-tile-add-to-cart"
                      :data-test-2="
                        !bestPrice || isAddingItemToCart[product.id] || outOfStock
                          ? 'product-tile-add-to-cart-disabled'
                          : null
                      "
                      @click.stop.prevent="addProduct"
                    >
                      <template v-if="showWithoutPrice">
                        {{ $dict.get('online-Shop.Product-Teaser.Button-Add-To-Cart') }}
                      </template>

                      <icon-fi
                        v-else
                        class="static-product-tile-fi__action-button__icon"
                        icon="shopping-cart"
                        color="white"
                      />
                    </button-fi>
                  </div>
                </div>

                <div class="static-product-tile-fi__quantity-stepper">
                  <quantity-steps-updater
                    v-if="currentBestPrice"
                    ref="quantitySteps"
                    _:ref="(ref) => assignQuantityStepsRef(product?.id, ref)"
                    :item="currentBestPrice"
                    :requested-quantity="0"
                    auto-width
                    @update-quantity="$emit('update-best-price', $event)"
                    @quantity-input="$emit('quantity-input', $event)"
                    @value-rounded="onValueRounded"
                  />

                  <product-package-size-fi
                    class="static-product-tile-fi__base-price"
                    :item="currentBestPrice"
                    align-right
                  />

                  <quantity-modification-hint-fi
                    v-if="quantityIncreasedMessage"
                    class="static-product-tile-fi__quantity-modification-hint"
                    :hint="quantityIncreasedMessage"
                    :data-test-2="quantityDataTest2"
                    icon="info"
                    theme="static-product-tile-fi"
                  />
                </div>
              </div>
              <div
                v-if="!bestPriceLoading && !isSM"
                class="static-product-tile-fi__add-to-cart"
              >
                <button-fi
                  :data-productid="product ? product.id : null"
                  :data-productname="product ? `${product.id} - ${product.name}` : null"
                  :disabled="!bestPrice || isAddingItemToCart[product.id] || outOfStock"
                  type="button"
                  class="button-fi__primary button-fi--stretched static-product-tile-fi__action-button static-product-tile-fi__action-button--onlineShop"
                  data-test="product-tile-add-to-cart"
                  @click.stop.prevent="addProduct"
                >
                  {{ $dict.get('online-Shop.Product-Teaser.Button-Add-To-Cart') }}
                </button-fi>
              </div>
            </loading-indicator-wrapper>
          </div>
        </template>

        <button-fi
          v-else-if="$fischer.shop.isRetailConnectCart()"
          :data-productid="product ? product.id : null"
          :data-productname="product ? `${product.id} - ${product.name}` : null"
          :disabled="productInCart || isAddingItemToCart[product.id] || outOfStock"
          data-test="product-tile-add-to-cart"
          type="button"
          class="button-fi__primary button-fi--stretched static-product-tile-fi__action-button static-product-tile-fi__action-button--retailConnectCart"
          @click.stop.prevent="addProduct"
        >
          {{ $dict.get('online-Shop.Product-Teaser.Button-Add-To-Cart') }}
        </button-fi>

        <button-fi
          v-else
          :data-productid="product ? product.id : null"
          :data-productname="product ? `${product.id} - ${product.name}` : null"
          data-test="product-tile-to-cart"
          type="button"
          class="button-fi__primary button-fi--stretched static-product-tile-fi__action-button static-product-tile-fi__action-button--retailConnect"
        >
          {{ $dict.get('online-Shop.Product-Teaser.Button-Product-Text') }}
        </button-fi>
      </template>
      <template v-else>
        <div class="static-product-tile-fi__uvp-price">
          <div
            v-if="shopifyPrice"
            class="static-product-tile-fi__uvp-price__value"
            data-test="product-price"
          >
            {{ shopifyPrice }}
          </div>
          <div
            v-else-if="productPrice"
            class="static-product-tile-fi__uvp-price__value"
            data-test="product-price"
          >
            {{ $dict.get('online-Shop.Product.UVP-Price-Prefix') }} {{ productPrice }}
          </div>

          <div class="static-product-tile-fi__uvp-price__vat">
            {{ $dict.get('online-Shop.Product.Vat-included') }}
          </div>
        </div>
      </template>
    </div>
  </component>
</template>

<script setup lang="ts">
import {
  AvailabilityStatus,
  ButtonFi,
  CloudImageFi,
  IconFi,
  LoadingIndicatorWrapper,
  PriceSavingsFi,
  ProductPackageSizeFi,
  QuantityModificationHintFi,
} from 'atoms';
import { computed, ref, useTemplateRef } from 'vue';
import { useStore } from 'vuex';

import QuantityStepsUpdater from '@/components/shop-modules/quantity-steps-updater/QuantityStepsUpdater.vue';
import { useBreakpoints } from '@/composables/breakpoint';
import { usePriceCurrency } from '@/composables/priceCurrency';
import { useShopifyProduct } from '@/composables/shopifyProduct';
import type { HybrisFischerBestPrice } from '@/generated/graphql';

import type { TileProduct } from '../ProductTileFi.vue';
const store = useStore();
const { formatPrice } = usePriceCurrency();

defineOptions({
  name: 'StaticProductTileFi',
});

const emit = defineEmits<{
  'add-product': [quantity: number];
  'go-to-product': [quantity: number];
  'update-best-price': [quantity: number];
  'quantity-input': [value: void];
}>();

const props = defineProps<{
  product: TileProduct;
  href: string | undefined;
  productInCart: boolean;
  isShop: boolean;
  showWithoutPrice: boolean;
  currentBestPrice: HybrisFischerBestPrice | undefined;
  productPrice: string | null;
  bestPriceLoading: boolean;
  bestPrice: HybrisFischerBestPrice | null | undefined;
  isAddingItemToCart: Record<string, boolean>;
  outOfStock: boolean | undefined;
  loading: boolean;
}>();

const { isSM } = useBreakpoints();

const quantitySteps = useTemplateRef<typeof QuantityStepsUpdater>('quantitySteps');
const quantityIncreasedMessage = ref<string | null>(null);
const quantityDataTest2 = ref<string | undefined>(undefined);
const shopifyProducts = computed(() => store.getters['shopify/shopifyProducts']);
const { findPriceBySku } = useShopifyProduct(shopifyProducts);
const shopifyPrice = computed((): string | null => {
  const priceInfo = findPriceBySku(props.product.id);
  if (priceInfo?.price?.amount) {
    const amount = parseFloat(priceInfo.price.amount);
    return formatPrice(amount, priceInfo.price.currencyCode);
  }
  return null;
});

const getItemQuantity = () => {
  if (!quantitySteps.value) {
    return 0;
  }
  return quantitySteps.value.internalRequestedQuantity
    ? quantitySteps.value.internalRequestedQuantity
    : quantitySteps.value.item.initialQuantity;
};

const addProduct = () => {
  emit('add-product', getItemQuantity());
};

const onValueRounded = (hint: string, dataTest2: string) => {
  quantityIncreasedMessage.value = hint;
  quantityDataTest2.value = dataTest2;
};
</script>

<style scoped src="./static-product-tile-fi" />
